/**
 * MeetingDetailSection - Meeting-specific content for the slideout.
 *
 * Renders time-sensitive content after the shared Overview section:
 * - **Future meetings**: Meeting prep brief (AI-generated markdown)
 * - **Past meetings**: Meeting summaries/transcripts (Google Meet, Zoom, etc.)
 *
 * Attendees and meeting link are rendered in SummarySection (above Overview).
 * Used by TypeSpecificSection for ahoy_meeting entities.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Edit05, RefreshCcw01 } from '@untitledui/icons';
import React, { useCallback, useMemo } from 'react';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';

import { Button } from '@/lib/base/buttons/button';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { QueryErrorState } from '@/lib/components';
import { MarkdownContent } from '@/lib/crm/components/MarkdownContent';
import { useCrmObjectSnapshots } from '@/lib/crm/hooks/useCrmObjectSnapshots';
import {
  FOCUSED_SECTION_MEETING_PREP,
  useFocusedSectionScroll,
} from '@/lib/crm/hooks/useFocusedAssociationScroll';
import { apiPost } from '@/lib/http/client';
import { logger } from '@/lib/logger';
// `entitySanitizeSchema` + `preprocessEntityTags` come from `entitySanitize`
// instead of `entityRendering` to avoid the EntityBadgeById → registry →
// section import cycle. See lib/markdown/entitySanitize.ts for the full
// explanation.
import { createEntityMarkdownComponents } from '@/lib/markdown/entityRendering';
import {
  entitySanitizeSchema,
  preprocessEntityTags,
} from '@/lib/markdown/entitySanitize';
import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

import { MeetingNotesTab } from '../components/MeetingNotesTab';
import { useIsMeetingPast } from '../hooks/useIsMeetingPast';
import { useMeetingDeals } from '../hooks/useMeetingDeals';
import { extractResearchData, type Research } from '../research/useResearch';
import { useMeetingPrepBrief } from '../useMeetingPrepBrief';
import type { MeetingPrepViewDefinition } from '../utils/meetingPrepTypes';
import {
  deriveSectionsFromFlatMetadata,
  getSectionExpanded,
  isValidViewDefinition,
} from '../utils/meetingPrepTypes';
import {
  type ParsedMeetingPrep,
  parseMeetingPrepMarkdown,
} from '../utils/parseMeetingPrepMarkdown';
import { CollapsibleSection } from './collapsible-section';
import { DealStatusSection } from './deal-status-section';
import { MeetingPrepCompanyIntelSection } from './meeting-prep-company-intel-section';
import { MeetingPrepPeopleSection } from './meeting-prep-people-section';
import { MeetingPrepSummarySection } from './meeting-prep-summary-section';
import { PreviousInteractionsSection } from './previous-interactions-section';

// ============================================================================
// Static config (hoisted to avoid new references on every render)
// ============================================================================

const REHYPE_PLUGINS = [
  rehypeRaw,
  [rehypeSanitize, entitySanitizeSchema],
] as unknown[];

// ============================================================================
// Props
// ============================================================================

interface MeetingDetailSectionProps {
  object: CrmObject;
  /** Callback to trigger note creation in the NotesSection. */
  onRequestNoteCreation?: () => void;
}

// ============================================================================
// Types
// ============================================================================

interface GeneratePrepResponse {
  entity_id: string;
  created: boolean;
  status: string;
}

// ============================================================================
// Sub-components
// ============================================================================

/** Section header for prep brief content sections (uses brand color) */
function PrepSectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <h3 className='text-[14px] font-semibold text-fg-brand-primary'>
      {children}
    </h3>
  );
}

/** Loading skeleton for the prep content */
function PrepLoadingSkeleton() {
  return (
    <div className='animate-pulse space-y-6'>
      <div className='space-y-2'>
        <div className='h-4 w-28 bg-[var(--color-bg-tertiary)] rounded' />
        <div className='h-4 bg-[var(--color-bg-tertiary)] rounded w-full' />
        <div className='h-4 bg-[var(--color-bg-tertiary)] rounded w-3/4' />
      </div>
      <div className='space-y-2'>
        <div className='h-4 w-24 bg-[var(--color-bg-tertiary)] rounded' />
        <div className='h-4 bg-[var(--color-bg-tertiary)] rounded w-full' />
        <div className='h-4 bg-[var(--color-bg-tertiary)] rounded w-5/6' />
      </div>
    </div>
  );
}

/** Empty state actions: Add note + Generate prep. */
function PrepEmptyActions({
  onAddNote,
  onGenerate,
  isGenerating,
  generateError,
  showGenerate,
}: {
  onAddNote?: () => void;
  onGenerate: () => void;
  isGenerating: boolean;
  generateError: Error | null;
  showGenerate: boolean;
}) {
  return (
    <div className='flex flex-col items-center gap-2 py-4'>
      <p className='text-xs md:text-sm text-fg-tertiary mb-1'>
        No prep brief available
      </p>
      <div className='flex items-center gap-3'>
        {onAddNote && (
          <Button
            color='secondary'
            size='sm'
            onClick={onAddNote}
            iconLeading={Edit05}
          >
            Add note
          </Button>
        )}
        {showGenerate && (
          <Button
            color='secondary'
            size='sm'
            onClick={onGenerate}
            isDisabled={isGenerating}
            isLoading={isGenerating}
            showTextWhileLoading
            iconLeading={isGenerating ? undefined : RefreshCcw01}
          >
            {isGenerating ? 'Generating…' : 'Generate prep brief'}
          </Button>
        )}
      </div>
      {generateError && (
        <p className='text-xs text-utility-error-600'>
          We couldn't generate the prep brief. Try again.
        </p>
      )}
    </div>
  );
}

// ============================================================================
// Prep Brief Section (future meetings)
// ============================================================================

function PrepBriefContent({
  parsedPrep,
  orgId,
}: {
  parsedPrep: ParsedMeetingPrep;
  orgId: number;
}) {
  const markdownComponents = useMemo(
    () => createEntityMarkdownComponents(orgId),
    [orgId]
  );

  return (
    // Stamped as the `?focusedSection` scroll target so the dashboard's
    // "View full prep" lands here. The wrapper is needed because
    // CollapsibleSection does not forward arbitrary props.
    <div data-focused-section={FOCUSED_SECTION_MEETING_PREP}>
      <CollapsibleSection title='Meeting prep' defaultExpanded>
        <div className='space-y-4 pt-1'>
          {parsedPrep.preamble && (
            <div className='text-xs md:text-sm text-fg-secondary leading-relaxed'>
              <MarkdownContent
                content={preprocessEntityTags(parsedPrep.preamble)}
                size='compact'
                rehypePlugins={REHYPE_PLUGINS}
                components={markdownComponents}
                className='[&>*:first-child]:mt-0 [&>*:last-child]:mb-0'
              />
            </div>
          )}
          {parsedPrep.sections.map(section => (
            <div key={section.title}>
              <PrepSectionHeader>
                {/*
                Section titles can contain inline <entity> tags (the LLM is
                instructed to embed entity references throughout its output,
                including ## headings). Render the title through the same
                preprocess + MarkdownContent pipeline as the body so entity
                tags become badges instead of leaking through as literal text.
                `inline [&>p]:inline` collapses MarkdownContent's div + p
                wrappers so the heading stays a single line.
              */}
                <MarkdownContent
                  content={preprocessEntityTags(section.title)}
                  size='compact'
                  rehypePlugins={REHYPE_PLUGINS}
                  components={markdownComponents}
                  className='inline [&>p]:inline'
                  as='span'
                />
              </PrepSectionHeader>
              {section.content && (
                <div className='mt-2 text-xs md:text-sm text-fg-secondary leading-relaxed'>
                  <MarkdownContent
                    content={preprocessEntityTags(section.content)}
                    size='compact'
                    rehypePlugins={REHYPE_PLUGINS}
                    components={markdownComponents}
                    className='[&>*:first-child]:mt-0 [&>*:last-child]:mb-0'
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </CollapsibleSection>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function MeetingDetailSection({
  object,
  onRequestNoteCreation,
}: MeetingDetailSectionProps) {
  const orgId = object.org_id;
  const meetingEntityId = object.entity_id;
  const queryClient = useQueryClient();

  // Meeting times
  const startTime = getPropertyValue<string>(
    object.properties?.ahoy_start_time
  );
  const endTime = getPropertyValue<string>(object.properties?.ahoy_end_time);

  // Determine if the meeting is in the past. See useIsMeetingPast for details
  // on the one-shot-timer strategy that avoids per-render `Date.now()` work.
  const isMeetingPast = useIsMeetingPast(endTime || startTime);

  // Fetch prep briefs (for both past and future meetings — past meetings can
  // still have a brief worth showing alongside the notes/transcript).
  const {
    data: prepBriefs,
    isLoading: isPrepLoading,
    error: prepError,
    refetch: refetchPrep,
  } = useMeetingPrepBrief(orgId, meetingEntityId);

  // On-demand prep generation
  const generateMutation = useMutation({
    mutationFn: async () => {
      return apiPost<GeneratePrepResponse>(
        `/api/orgs/${orgId}/meetings/${meetingEntityId}/generate-prep/`,
        {}
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['meetingPrep', orgId, meetingEntityId],
      });
    },
    onError: error => {
      logger.error('Failed to generate meeting prep', error, {
        component: 'crm_events',
        meeting_entity_id: meetingEntityId,
        error_type: error.name,
      });
    },
  });

  const { mutate: generateMutate } = generateMutation;
  const handleGenerate = useCallback(() => {
    generateMutate();
  }, [generateMutate]);

  const handleRetryPrep = useCallback(() => {
    refetchPrep();
  }, [refetchPrep]);

  // Parse view_definition (structured v2.0 format)
  const viewDefinition = useMemo((): MeetingPrepViewDefinition | null => {
    if (!prepBriefs || prepBriefs.length === 0) {
      return null;
    }
    const viewDefStr = getPropertyValue<string>(
      prepBriefs[0].properties?.ahoy_view_definition
    );
    if (!viewDefStr) {
      return null;
    }
    try {
      const parsed: unknown = JSON.parse(viewDefStr);
      if (isValidViewDefinition(parsed) && parsed.version === '2.0') {
        return parsed;
      }
    } catch (e) {
      logger.debug(
        'View definition parse failed, falling back to legacy format',
        { component: 'crm_events', error: String(e) }
      );
    }
    return null;
  }, [prepBriefs]);

  // Parse prep brief markdown (legacy fallback)
  const parsedPrep = useMemo(() => {
    if (viewDefinition) {
      return null;
    }
    if (!prepBriefs || prepBriefs.length === 0) {
      return null;
    }
    const summary = getPropertyValue<string>(
      prepBriefs[0].properties?.ahoy_summary
    );
    if (!summary) {
      return null;
    }
    return parseMeetingPrepMarkdown(summary);
  }, [prepBriefs, viewDefinition]);

  // Deep-link affordance for the dashboard's "View full prep" button, which
  // opens this slideout with `?focusedSection=meeting_prep`.
  //
  // Gated on prep CONTENT being rendered, not merely on the query settling:
  // the hook resolves its target by querySelector, so the stamped element has
  // to exist. The first two terms mean it does this render. `!isPrepLoading`
  // bounds the wait — the loading skeleton is the only branch that renders no
  // stamped element, so once loading ends the hook can consume the param even
  // when there is no brief to scroll to, rather than leaving it in the URL.
  //
  // Must precede the `isMeetingPast` early return below (Rules of Hooks).
  useFocusedSectionScroll(
    Boolean(viewDefinition) || Boolean(parsedPrep) || !isPrepLoading
  );

  // Prep brief status (for in_progress / failed states)
  const prepStatus = useMemo(() => {
    if (!prepBriefs || prepBriefs.length === 0) {
      return null;
    }
    return (
      getPropertyValue<string>(prepBriefs[0].properties?.ahoy_status) ||
      'in_progress'
    );
  }, [prepBriefs]);

  // Structured panel data (only computed when viewDefinition exists)
  const sections = useMemo(() => {
    if (!viewDefinition) {
      return {};
    }
    if (
      viewDefinition.sections &&
      Object.keys(viewDefinition.sections).length > 0
    ) {
      return viewDefinition.sections;
    }
    if (viewDefinition.section_metadata) {
      return deriveSectionsFromFlatMetadata(viewDefinition.section_metadata);
    }
    return {};
  }, [viewDefinition]);

  const rsvpMap = useMemo(() => {
    const map = new Map<string, string>();
    try {
      const invitations = getPropertyValue<
        Array<{ email?: string; response_status?: string }>
      >(object.properties?.ahoy_invitations);
      if (Array.isArray(invitations)) {
        for (const inv of invitations) {
          if (inv?.email && inv?.response_status) {
            map.set(inv.email.toLowerCase(), inv.response_status);
          }
        }
      }
    } catch {
      // Malformed invitations — render people without RSVP badges
    }
    return map;
  }, [object.properties?.ahoy_invitations]);

  const people = useMemo(
    () => viewDefinition?.people ?? [],
    [viewDefinition?.people]
  );
  const companies = useMemo(
    () => viewDefinition?.companies ?? [],
    [viewDefinition?.companies]
  );

  const researchEntityIds = useMemo(() => {
    const ids: string[] = [];
    for (const p of people) {
      if (p.research_entity_id) {
        ids.push(p.research_entity_id);
      }
    }
    for (const c of companies) {
      if (c.research_entity_id) {
        ids.push(c.research_entity_id);
      }
    }
    return [...new Set(ids)].slice(0, 100);
  }, [people, companies]);

  const researchObjects = useCrmObjectSnapshots(researchEntityIds, orgId);

  const researchById = useMemo(() => {
    const map = new Map<string, Research>();
    for (const [id, obj] of researchObjects) {
      if (obj) {
        map.set(id, extractResearchData(obj));
      }
    }
    return map;
  }, [researchObjects]);

  const {
    activeDeals: meetingActiveDeals,
    isLoading: isDealStatusLoading,
    isError: isDealStatusError,
  } = useMeetingDeals(object, !!viewDefinition);

  // Show the People / Company sections only when at least one attendee /
  // company has usable research — i.e. a research entry that's not in the
  // `failed` state. While the object is not yet in the store we keep the
  // section visible (so the skeleton can render and the layout doesn't shift
  // when data arrives). Once the object is loaded and has failed status,
  // the section hides.
  const hasUsableResearch = useCallback(
    (researchEntityId: string | null): boolean => {
      if (!researchEntityId) {
        return false;
      }
      // Not yet subscribed or not yet fetched — treat as available while loading.
      if (
        !researchObjects.has(researchEntityId) ||
        researchObjects.get(researchEntityId) === undefined
      ) {
        return true;
      }
      const research = researchById.get(researchEntityId);
      if (!research) {
        return false;
      }
      return research.status !== 'failed';
    },
    [researchObjects, researchById]
  );

  const hasPeopleResearch = useMemo(
    () => people.some(p => hasUsableResearch(p.research_entity_id)),
    [people, hasUsableResearch]
  );
  const hasCompanyResearch = useMemo(
    () => companies.some(c => hasUsableResearch(c.research_entity_id)),
    [companies, hasUsableResearch]
  );

  const hasActionItems = (viewDefinition?.action_items?.length ?? 0) > 0;

  // The structured v2.0 prep sections — used for both past and future meetings
  // when a valid v2.0 brief is available.
  const prepSections = viewDefinition ? (
    <>
      {/* The summary is unconditionally first, so stamping it (rather than
          wrapping the whole fragment) lands the `?focusedSection` scroll at
          the top of prep while leaving the child count — and therefore the
          slideout's `gap-4` column flow — untouched. */}
      <div data-focused-section={FOCUSED_SECTION_MEETING_PREP}>
        <MeetingPrepSummarySection
          aiSummary={viewDefinition.ai_summary}
          defaultExpanded={getSectionExpanded(viewDefinition.mode, 'summary')}
          metadata={sections['summary']?.section_metadata?.text}
          orgId={orgId}
        />
      </div>
      {!isDealStatusLoading &&
        (meetingActiveDeals.length > 0 || isDealStatusError) && (
          <DealStatusSection
            defaultExpanded={getSectionExpanded(
              viewDefinition.mode,
              'deal_status'
            )}
            metadata={sections['deal_status']?.section_metadata?.text}
            object={object}
          />
        )}
      {hasActionItems && (
        <PreviousInteractionsSection
          defaultExpanded={getSectionExpanded(
            viewDefinition.mode,
            'previous_interactions'
          )}
          metadata={sections['previous_interactions']?.section_metadata?.text}
          viewDefinition={viewDefinition}
        />
      )}
      {hasPeopleResearch && (
        <MeetingPrepPeopleSection
          people={people}
          rsvpMap={rsvpMap}
          defaultExpanded={getSectionExpanded(viewDefinition.mode, 'people')}
          metadata={
            sections['people']?.section_metadata?.text ??
            `${people.length} attendee${people.length !== 1 ? 's' : ''}`
          }
          researchById={researchById}
        />
      )}
      {hasCompanyResearch && (
        <MeetingPrepCompanyIntelSection
          companies={companies}
          defaultExpanded={getSectionExpanded(
            viewDefinition.mode,
            'company_intel'
          )}
          metadata={sections['company_intel']?.section_metadata?.text}
          researchById={researchById}
        />
      )}
    </>
  ) : null;

  if (isMeetingPast) {
    // Past meeting: show notes/transcripts first, then any available prep
    // content — either the structured v2.0 sections OR the legacy markdown
    // brief, whichever the backend wrote. We skip the loading skeleton,
    // generate-prep CTA, and empty-prep affordance — those are only useful
    // before the meeting happens. But if the prep brief itself exists, it
    // stays useful afterward (it's the AI's record of what was on the
    // agenda, who was new, what to follow up on).
    return (
      <>
        <CollapsibleSection title='Meeting summaries' defaultExpanded>
          <MeetingNotesTab
            orgId={orgId}
            meetingEntityId={meetingEntityId}
            meetingStartTime={startTime}
          />
        </CollapsibleSection>
        {prepSections}
        {parsedPrep && (
          <PrepBriefContent parsedPrep={parsedPrep} orgId={orgId} />
        )}
      </>
    );
  }

  // Future meeting with a structured v2.0 prep brief
  if (prepSections) {
    return prepSections;
  }

  // Future meeting: legacy / loading / error / empty states
  return (
    <>
      {isPrepLoading && <PrepLoadingSkeleton />}

      {/* Prep query error */}
      {prepError && !isPrepLoading && (
        <CollapsibleSection title='Meeting prep' defaultExpanded>
          <QueryErrorState
            error={prepError}
            label="We couldn't load the prep brief"
            onRetry={handleRetryPrep}
            size='sm'
          />
        </CollapsibleSection>
      )}

      {/* Prep brief in progress */}
      {prepStatus === 'in_progress' && !parsedPrep && (
        <CollapsibleSection title='Meeting prep' defaultExpanded>
          <div className='flex items-center gap-3 p-3 bg-[var(--color-bg-secondary)] rounded-lg'>
            <LoadingIndicator size='xs' inline />
            <p className='text-xs md:text-sm text-fg-secondary'>
              Prep brief is being generated…
            </p>
          </div>
        </CollapsibleSection>
      )}

      {/* Prep brief failed */}
      {prepStatus === 'failed' && !parsedPrep && (
        <CollapsibleSection title='Meeting prep' defaultExpanded>
          <div className='flex items-start gap-2 p-3 bg-utility-error-50 border border-utility-error-200 rounded-lg'>
            <AlertCircle className='size-4 shrink-0 mt-0.5 text-utility-error-600' />
            <div className='flex flex-col gap-2'>
              <p className='text-xs md:text-sm text-utility-error-700'>
                We couldn't generate the prep brief
              </p>
              <Button
                color='secondary'
                size='sm'
                onClick={handleGenerate}
                isDisabled={generateMutation.isPending}
                iconLeading={RefreshCcw01}
              >
                {generateMutation.isPending ? 'Retrying…' : 'Retry'}
              </Button>
            </div>
          </div>
        </CollapsibleSection>
      )}

      {/* Prep brief content (legacy markdown) */}
      {parsedPrep && <PrepBriefContent parsedPrep={parsedPrep} orgId={orgId} />}

      {/* Completed prep brief with empty summary — offer regenerate */}
      {prepStatus &&
        prepStatus !== 'in_progress' &&
        prepStatus !== 'failed' &&
        !parsedPrep &&
        !viewDefinition &&
        prepBriefs &&
        prepBriefs.length > 0 && (
          <CollapsibleSection title='Meeting prep' defaultExpanded>
            <PrepEmptyActions
              onAddNote={onRequestNoteCreation}
              onGenerate={handleGenerate}
              isGenerating={generateMutation.isPending}
              generateError={generateMutation.error}
              showGenerate={true}
            />
          </CollapsibleSection>
        )}

      {/* No prep brief — offer Add note + Generate */}
      {!prepBriefs?.length && !isPrepLoading && !prepError && (
        <CollapsibleSection title='Meeting prep' defaultExpanded>
          <PrepEmptyActions
            onAddNote={onRequestNoteCreation}
            onGenerate={handleGenerate}
            isGenerating={generateMutation.isPending}
            generateError={generateMutation.error}
            showGenerate={true}
          />
        </CollapsibleSection>
      )}
    </>
  );
}
